// ═══════════════════════════════════════════════════════════════════════════
// Existencias en negativo — se avisa, no se bloquea
//
// EL CRITERIO ES DEL OPERADOR, y cambia lo que significa el número:
//
//   "De la sobreventa, solo un warning. Si se vendió hay botellas que no se
//    registraron."
//
// Es la lectura correcta y hay que dejarla escrita, porque invita a lo
// contrario. Un stock negativo NO dice "vendiste vino que no tienes" — el vino
// salió, el cliente lo tiene. Dice que **entraron botellas que nadie registró**:
// una producción, una devolución, un ajuste de bodega. El faltante está en los
// movimientos de ENTRADA, no en la venta.
//
// Por eso bloquear la venta sería el arreglo equivocado: pararía lo único que
// sí ocurrió de verdad para proteger un número que ya estaba mal.
//
// EL DEFECTO QUE ESTO CORRIGE. La pantalla de Productos decía:
//
//     (p.stock || 0) > 0 ? `${p.stock} en stock` : "Sin stock"
//
// así que un producto en −2 se veía EXACTAMENTE IGUAL que uno en 0: "Sin
// stock". El hecho de que faltaran botellas por registrar no estaba en ninguna
// pantalla. Un aviso que no se ve no es un aviso.
// ═══════════════════════════════════════════════════════════════════════════

const cantidad = (p) => Math.trunc(Number(p?.stock) || 0);

/**
 * Los productos con existencias en negativo, con cuántas botellas faltan por
 * registrar. Ordenados por el faltante mayor: es a lo que hay que ir primero.
 */
export function faltanPorRegistrar(products = []) {
  return (products || [])
    .filter(p => cantidad(p) < 0)
    .map(p => ({ id: p.id, nombre: p.name, stock: cantidad(p), faltan: -cantidad(p) }))
    .sort((a, b) => b.faltan - a.faltan);
}

/** ¿Hay algo que avisar? */
export const hayFaltantes = (products = []) => faltanPorRegistrar(products).length > 0;

/**
 * Cuántas botellas hay de verdad, y cuántas faltan por registrar.
 *
 * Se cuentan APARTE a propósito. El total de la pantalla sumaba todo, así que
 * un −2 se restaba de las demás y desaparecía dentro del gran total: dos
 * defectos tapándose entre ellos. Las existencias reales no pueden ser
 * negativas, y lo que falta por registrar es otro hecho.
 */
export function resumenExistencias(products = []) {
  let botellas = 0, faltan = 0;
  for (const p of products || []) {
    const n = cantidad(p);
    if (n > 0) botellas += n;
    else if (n < 0) faltan += -n;
  }
  return { botellas, faltan, productosConFaltante: faltanPorRegistrar(products).length };
}

/**
 * Cómo se dice un stock en la pantalla. Devuelve el texto y el tono, para que
 * la frase viva junto a la regla y no repartida por los componentes.
 */
export function etiquetaExistencias(producto) {
  const n = cantidad(producto);
  if (n > 0)  return { texto: `${n} en stock`, tono: "bien" };
  if (n === 0) return { texto: "Sin stock", tono: "neutro" };
  return { texto: `${-n} sin registrar`, tono: "aviso" };
}

/** La explicación, en las palabras del operador. Una sola vez, aquí. */
export const POR_QUE_FALTAN =
  "Un negativo no significa que se haya vendido vino que no existe: el vino " +
  "salió y el cliente lo tiene. Significa que entraron botellas que nadie " +
  "registró —una producción, una devolución, un ajuste de bodega—. Se corrige " +
  "registrando la entrada que faltó, no cancelando la venta.";
